import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/shared/services/query-keys';
import { useUIStore } from '@/shared/stores/ui-store';
import { useAuthStore } from '@/shared/stores/auth-store';
import {
  connectAccount,
  disconnectAccount,
  setDefaultMetaPixel,
  syncAccount,
} from './accounts-api';

export function useConnectAccount() {
  const queryClient = useQueryClient();
  const addToast = useUIStore((s) => s.addToast);

  return useMutation({
    mutationFn: ({ platform, code }: { platform: string; code: string }) =>
      connectAccount(platform, code),
    retry: 0,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accounts.all() });
      addToast({ message: 'Account connected successfully', variant: 'success' });
    },
    onError: () => {
      addToast({ message: 'Failed to connect account', variant: 'error' });
    },
  });
}

export function useDisconnectAccount() {
  const queryClient = useQueryClient();
  const addToast = useUIStore((s) => s.addToast);

  return useMutation({
    mutationFn: disconnectAccount,
    retry: 0,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accounts.all() });
      addToast({ message: 'Account disconnected', variant: 'success' });
    },
    onError: () => {
      addToast({ message: 'Failed to disconnect account', variant: 'error' });
    },
  });
}

export function useSyncAccount() {
  const queryClient = useQueryClient();
  const addToast = useUIStore((s) => s.addToast);

  return useMutation({
    mutationFn: syncAccount,
    retry: 0,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accounts.all() });
    },
    onError: () => {
      addToast({ message: 'Failed to sync account', variant: 'error' });
    },
  });
}

/** Persist the default Meta Pixel on an ad account so the wizard prefills it next time. */
export function useSetDefaultMetaPixel() {
  const queryClient = useQueryClient();
  const addToast = useUIStore((s) => s.addToast);
  const orgId = useAuthStore((s) => s.orgId);

  return useMutation({
    mutationFn: setDefaultMetaPixel,
    retry: 0,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.accounts.detail(variables.adAccountId, orgId ?? undefined),
      });
      addToast({ message: 'Default pixel saved', variant: 'success' });
    },
    onError: () => {
      addToast({ message: 'Could not save default pixel', variant: 'error' });
    },
  });
}
